import React from "react";

import { Box, CircularProgress } from "@mui/material";
import Container from "@mui/material/Container";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback, SecureRoute, Security } from "@okta/okta-react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import AppRouterSearchContext from "./app-router-search-context";
import Navigation from "./components/navigation/navigation";
import OrgDeletionReminder from "./components/org-display/org-deletion-reminder";
import IdentityContextWrapper from "./context/identity-context-wrapper";
import OrganisationContextWrapper from "./context/organisation-context-wrapper";
import PermissionsContextWrapper from "./context/permissions-context-wrapper";
import UserContextWrapper from "./context/user-context-wrapper";
import { oktaAuth } from "./okta";
import AppSettings from "./pages/app-settings";
import CopyCourse from "./pages/copy-course";
import Identity from "./pages/identity";
import IdentityHistory from "./pages/identity-history";
import OrganisationHistory from "./pages/org-history";
import OrganisationSensors from "./pages/org-sensors";
import Organisation from "./pages/organisation";
import Reports from "./pages/reports";
import RetroShares from "./pages/retroshares";
import Search from "./pages/search";
import Sudo from "./pages/sudo";
import User from "./pages/user";
import mainStyles from "./styles";
import GlobalSearchDialog from "components/navigation/global-search-dialog";
import { DocPrefix } from "constants/index";
import FeatureFlagsContextWrapper from "context/feature-flags-context-wrapper";
import PaymentMethodsContextWrapper from "context/payment-methods-context-wrapper";
import SnackbarContextWrapper from "context/snackbar-context-wrapper";
import SubscriptionActionsContextWrapper from "context/subscriptions-action-context-wrapper";
import { toS12ID, validateUUID } from "helpers/s12-helpers";
import AdminTools from "pages/admin-tools";
import SupportServiceForm from "pages/dev-tools";
import LoadableDocumentVisualize from "pages/document-visualize/document-visualize.loadable";
import Marketplace from "pages/marketplace";
import OrganisationCustomLabels from "pages/org-custom-labels";
import OrganisationSites from "pages/org-sites";
import OrgUserJobDetails from "pages/org-user-job-details";
import OrgUserJobs from "pages/org-user-jobs";
import SCCare from "pages/sc-care";
import SeoTemplates from "pages/seo-templates";
import TemplateAccess from "pages/template-access";
import LoadableTemplateInspect from "pages/template-inspect/template-inspect.loadable";
import Workflows from "pages/workflows";

interface AppWithRouterAccessProps {
  isDarkMode: boolean;
  handleThemeToggle: () => void;
}

const AppWithRouterAccess = ({
  isDarkMode,
  handleThemeToggle,
}: AppWithRouterAccessProps) => {
  const history = useHistory();

  const urlParams = useLocation().pathname.split("/");
  if (urlParams[1] === "organisation" || urlParams[1] === "user") {
    if (validateUUID(urlParams[3])) {
      const prefix =
        urlParams[1] === "organisation"
          ? DocPrefix.PrefixRole
          : DocPrefix.PrefixUser;
      urlParams[3] = toS12ID(prefix, urlParams[3]);
      return <Redirect to={urlParams.join("/")} />;
    }
  }

  // implementation follows example in Okta docs
  // eslint-disable-next-line
  const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
    history.replace(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  const classes = mainStyles();
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Switch>
        <Route
          path="/implicit/callback"
          render={(props) => <LoginCallback {...props} />}
        />
        <Route
          render={() => {
            return (
              <SnackbarContextWrapper>
                <PermissionsContextWrapper>
                  <Navigation
                    isDarkMode={isDarkMode}
                    handleThemeToggle={handleThemeToggle}
                  />
                  <Box component="main" sx={classes.content}>
                    <Box sx={classes.appBarSpacer} />

                    <AppRouterSearchContext>
                      <GlobalSearchDialog />
                      <OrgDeletionReminder />
                      <Container maxWidth={false} sx={classes.container}>
                        <Switch>
                          <SecureRoute path="/organisation/:regionCode/:roleId/history">
                            <OrganisationHistory />
                          </SecureRoute>
                          <SecureRoute path="/organisation/:regionCode/:roleId/sensors">
                            <OrganisationSensors />
                          </SecureRoute>
                          <SecureRoute path="/organisation/:regionCode/:roleId/sites">
                            <OrganisationSites />
                          </SecureRoute>
                          <SecureRoute path="/organisation/:regionCode/:roleId/custom-labels">
                            <OrganisationCustomLabels />
                          </SecureRoute>
                          <SecureRoute path="/organisation/:regionCode/:roleId/import-history/:jobId">
                            <OrganisationContextWrapper>
                              <OrgUserJobDetails />
                            </OrganisationContextWrapper>
                          </SecureRoute>
                          <SecureRoute path="/organisation/:regionCode/:roleId/import-history">
                            <OrganisationContextWrapper>
                              <OrgUserJobs />
                            </OrganisationContextWrapper>
                          </SecureRoute>
                          <SecureRoute path="/dev-tools">
                            <SupportServiceForm />
                          </SecureRoute>
                          <SecureRoute path="/organisation/:regionCode/:roleId">
                            <OrganisationContextWrapper>
                              <FeatureFlagsContextWrapper>
                                <SubscriptionActionsContextWrapper>
                                  <PaymentMethodsContextWrapper>
                                    <Organisation />
                                  </PaymentMethodsContextWrapper>
                                </SubscriptionActionsContextWrapper>
                              </FeatureFlagsContextWrapper>
                            </OrganisationContextWrapper>
                          </SecureRoute>
                          <SecureRoute path="/sudo">
                            <Sudo />
                          </SecureRoute>
                          <SecureRoute path="/copy-course">
                            <CopyCourse />
                          </SecureRoute>
                          <SecureRoute path="/user/:regionCode/:userId">
                            <UserContextWrapper>
                              <User />
                            </UserContextWrapper>
                          </SecureRoute>
                          <SecureRoute path="/identity/:identityId/history">
                            <IdentityHistory />
                          </SecureRoute>
                          <SecureRoute path="/identity/:identityId">
                            <IdentityContextWrapper>
                              <Identity />
                            </IdentityContextWrapper>
                          </SecureRoute>
                          <SecureRoute path="/settings">
                            <AppSettings />
                          </SecureRoute>
                          <SecureRoute path="/reports">
                            <Reports />
                          </SecureRoute>
                          <SecureRoute path="/admin-tools">
                            <AdminTools />
                          </SecureRoute>
                          <SecureRoute path="/seo-templates">
                            <SeoTemplates />
                          </SecureRoute>
                          <SecureRoute path="/workflows">
                            <Workflows />
                          </SecureRoute>
                          <SecureRoute exact path="/search">
                            <Redirect to="/" />
                          </SecureRoute>
                          <SecureRoute path="/marketplace">
                            <Marketplace />
                          </SecureRoute>
                          <SecureRoute path="/sc-care">
                            <SCCare />
                          </SecureRoute>
                          <SecureRoute path="/retroshare">
                            <RetroShares />
                          </SecureRoute>
                          <SecureRoute path="/template/:regionCode/:templateID/access">
                            <TemplateAccess />
                          </SecureRoute>
                          <SecureRoute path="/inspect/:docType/:regionCode/:docId">
                            <React.Suspense fallback={<CircularProgress />}>
                              <LoadableTemplateInspect />
                            </React.Suspense>
                          </SecureRoute>
                          <SecureRoute path="/visualize/:docType/:regionCode/:docId">
                            <React.Suspense fallback={<CircularProgress />}>
                              <LoadableDocumentVisualize />
                            </React.Suspense>
                          </SecureRoute>
                          <SecureRoute path="/">
                            <Search />
                          </SecureRoute>
                        </Switch>
                      </Container>
                    </AppRouterSearchContext>
                  </Box>
                </PermissionsContextWrapper>
              </SnackbarContextWrapper>
            );
          }}
        />
      </Switch>
    </Security>
  );
};
export default AppWithRouterAccess;
